import React, { useCallback, useContext, useMemo, useState } from "react";
import { Lists } from "../API/Lists";
import { Products } from "../API/Product";
import { Publications } from "../API/Publications";
import { deepClone } from "../API/utils/deepClone";

type ManageAccount = {
  productOptions: any[];
  getProductOptions: Function;
  availableSubscriptionsTypes: any;
  productCode: string;
  setProductCode: Function;
  subscriptions: any;
  setSubscriptions: Function;
  getSubscriptions: Function;
  setAccountID: Function;
  retrieveSubscriptionsForSaving: Function;
};

/*
This hook is used in the Subscriptions component to manage the state of the account being managed.
It provides functions to get and set the subscriptions of the account, as well as to get the available subscription types based on the product code of the account.

*/

const ManageAccountContext = React.createContext<ManageAccount>(null!);

export const useManageAccount = () => {
  return useContext(ManageAccountContext);
};

export const ManageAccountProvider = ({ children }: any) => {
  const filter = useProvideManageAccount();
  return (
    <ManageAccountContext.Provider value={filter}>
      {children}
    </ManageAccountContext.Provider>
  );
};

const useProvideManageAccount = () => {
  const listAPI = useMemo(() => new Lists(), []);
  const publicationsAPI = useMemo(() => new Publications(), []);
  const productsAPI = useMemo(() => new Products(), []);
  const [productOptions, setProductOptions] = useState([]);
  const getProductOptions = useCallback(
    async (
      level: "SUBSCRIBER" | "TRIAL" | "LEAD" | "INTERNAL" | "PARTNER" | "FREE"
    ) => {
      let _products = await productsAPI.getAllProducts();
      _products = _products.data.products;

      const allowedProducts: string[] = [];
      switch (level) {
        case "LEAD":
          break;
        case "FREE":
          allowedProducts.push("DISPERSION");
          break;
        case "INTERNAL":
        case "PARTNER":
        case "SUBSCRIBER":
        case "TRIAL":
          allowedProducts.push(
            "STANDARD",
            "PREMIUM",
            "ADVANCED",
            "SYSTEMATIC_ENGINE",
            "SYSTEMATIC_ENGINE_AND_PREMIUM",
            "RISE"
          );
          break;
        default:
          break;
      }

      _products = _products.filter((prod: any) =>
        allowedProducts.includes(prod.productCode)
      );

      if (_products.length > 0) {
        _products = _products.map((item: any) => ({
          label: item.codeName,
          value: item.id,
          //#region - added productCode field here to avoid requests on open subscrioptions
          productCode: item.productCode,
          //#endregion
        }));
      }

      _products.sort((a: any, b: any) =>
        a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
      );

      setProductOptions(_products);
      return _products;
    },
    [productsAPI]
  );
  const [accountID, setAccountID] = useState(null);
  const [productCode, setProductCode] = useState<any>(null);
  const availableSubscriptionsTypes = useMemo<any>(() => {
    const temp = ["list"];
    switch (productCode) {
      case "ADVANCED": {
        temp.push("strategy");
        temp.push("ranking");
        break;
      }
      case "SYSTEMATIC_ENGINE": {
        temp.push("strategy");
        temp.push("systematicPortfolio");
        break;
      }
      case "SYSTEMATIC_ENGINE_AND_PREMIUM": {
        temp.push("strategy");
        temp.push("systematicPortfolio");
        temp.push("ranking");
        break;
      }
      default: {
        temp.push("ranking");
        break;
      }
    }
    return temp;
  }, [productCode]);
  const [subscriptions, setSubscriptions] = useState<any>(null);
  const [startingSubscriptions, setStartingSubscriptions] = useState<any>(null);
  const getSubscriptions = useCallback(() => {
    // it's possible that accountID is null: if one wants to create a new
    // account it will open the modal with no user selected, then accountID will be null.
    // if no accountID is set, we can't fetch the subscriptions
    // so we reset the subscriptions and return null
    if (!accountID) {
      setStartingSubscriptions(null);
      setSubscriptions(null);
      return null;
    }

    try {
      return publicationsAPI
        .getSubscriptions({
          accountId: accountID,
          objectsType: availableSubscriptionsTypes,
        })
        .then(async (response) => {
          let _response = deepClone(response);
          // #region - make a distinction between portfolios and basket.
          if (_response.list) {
            // since response is an object like {list:[id1,....,idn]} we can't make a distinction between
            // portfolios and basket. to do so we need to fetch the properties of the ids.
            const lists = await listAPI.portfolioFetch(_response.list, [
              "type",
            ]);
            const _portfolios = lists
              .filter((item: any) => item.type === "PORTFOLIO")
              .map((item: any) => item.id);
            const _basket = lists
              .filter((item: any) => item.type === "BASKET")
              .map((item: any) => item.id);
            delete _response.list;
            _response.portfolios = _portfolios;
            _response.baskets = _basket;
          }
          //#endregion
          setStartingSubscriptions(_response);
          setSubscriptions(_response);
          return _response;
        });
    } catch (error) {
      throw new Error(error as any);
    }
  }, [accountID, availableSubscriptionsTypes, listAPI, publicationsAPI]);

  const retrieveSubscriptionsForSaving = useCallback(() => {
    // to subscribe => elementi che sono su subscriptions ma non su startingSubscription
    // to unsubscribe => elementi che sono su startingSubscription ma non su subscriptions
    // to ignore => elementi presenti nei due insiemi
    let toSubscribe: any = {};
    let toUnsubscribe: any = {};
    for (const subType in subscriptions) {
      let subscriptionSet: any = new Set(subscriptions[subType]);
      if (!startingSubscriptions) {
        // this means that we are creating a new account, so all the subscriptions are new
        toSubscribe[subType] = Array.from(subscriptionSet);
        toUnsubscribe[subType] = [];
        continue;
      }
      let startSubscriptionSet: any = new Set(startingSubscriptions[subType]);
      toSubscribe[subType] = Array.from(
        subscriptionSet.difference(startSubscriptionSet)
      );
      toUnsubscribe[subType] = Array.from(
        startSubscriptionSet.difference(subscriptionSet)
      );
    }
    return {
      toSubscribe: toSubscribe,
      toUnsubscribe: toUnsubscribe,
    };
  }, [startingSubscriptions, subscriptions]);

  return {
    productOptions,
    getProductOptions,
    availableSubscriptionsTypes,
    productCode,
    setProductCode,
    subscriptions,
    setSubscriptions,
    getSubscriptions,
    setAccountID,
    retrieveSubscriptionsForSaving,
  };
};
